import { TableColumnDef } from "@/types";
import { TreeNodeWithChildren } from "@/types/components/table.types";
import styled from "@emotion/styled";
import { ListItemText, Menu, MenuItem, SelectChangeEvent, Stack, Tooltip } from "@mui/material";
import {
  GridActionsCellItem,
  GridGroupNode,
  GridRenderCellParams,
  GridRowId,
  GridRowParams,
  useGridApiRef
} from "@mui/x-data-grid-pro";
import { ConfirmationDialog, ManageRegsEvent } from "klayowebshared";
import { debounce } from "lodash";
import React, { SyntheticEvent, useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { track } from "../../common/track";
import { Utils } from "../../common/Utils";
import { ErrorModel } from "../../common/models/ErrorModel";
import { Button } from "../../components/Button";
import { Paper } from "../../components/Paper";
import { SectionLoader } from "../../components/SectionLoader";
import { Snackbar } from "../../components/Snackbar";
import { TablePlaceholder } from "../../components/TablePlaceholder";
import { TextField } from "../../components/TextField";
import { RegulationsTable } from "../../components/table/RegulationsTable";
import { CREATE_TASK_MODE, REGULATION_ATTRIBUTE_TYPE } from "../../constants";
import { useLocale } from "../../providers/LocaleProvider";
import AddIcon from "../../resources/images/icons-add.svg";
import AttributesIcon from "../../resources/images/icons-attribute.svg";
import ChevronDownWhiteIcon from "../../resources/images/icons-chevron-down-white.svg";
import MoreVertIcon from "../../resources/images/icons-more-vertical.svg";
import SearchIcon from "../../resources/images/icons-search.svg";
import { KLAYO_COLORS } from "../../themes";
import type {
  CreateTaskModeType,
  DomainSaveData,
  KlayoTagProps,
  PreservedExpandedRow,
  Regulation,
  RegulationCategory,
  RegulationDomain,
  RegulationRegion,
  RegulationTableRow,
  SettingsRegulationsViewProps
} from "../../types/views/regulations.types";
import { highlightKeyword } from "../../utilities";
import { CreateRegulationCategoryDialog } from "../settings/CreateRegulationCategoryDialog";
import { CreateRegulationDomainDialog } from "../settings/CreateRegulationDomainDialog";
import { UpdateRegulationDialog } from "./UpdateRegulationDialog";

export function SettingsRegulationsView(props: SettingsRegulationsViewProps) {
  const { translate } = useLocale();
  const {
    history,
    theme,
    user,
    onGetListRegulationDomain,
    onGetListRegulationCategory,
    onGetListRegulationByCategoryId,
    listRegulationDomain,
    listAllRegulationDomain,
    onGetListRegulationRegion,
    onUpdateRegulation,
    onSaveRegulation,
    onBlockNavigation,
    onAllowNavigation,
    onNavigation
  } = props;

  const [selectedRowElement, setSelectedRowElement] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");
  const [domainAndCategoryActionMenuElement, setDomainAndCategoryActionMenuElement] =
    useState<HTMLElement | null>(null);
  const [isShowRegulationDomainDialog, setIsShowRegulationDomainDialog] = useState(false);
  const [createRegulationDomainMode, setCreateRegulationDomainMode] =
    useState<CreateTaskModeType | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<
    RegulationDomain | RegulationTableRow | null
  >(null);
  const [isShowRegulationCategoryDialog, setIsShowRegulationCategoryDialog] = useState(false);
  const [createRegulationCategoryMode, setCreateRegulationCategoryMode] =
    useState<CreateTaskModeType | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<
    RegulationCategory | RegulationTableRow | null
  >(null);
  const [regulationDomainError, setRegulationDomainError] = useState<string | null>(null);
  const [regulationCategoryError, setRegulationCategoryError] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<
    RegulationRegion | RegulationTableRow | null
  >(null);
  const [selectedRow, setSelectedRow] = useState<RegulationTableRow | null>(null);
  const [isDeleteRow, setIsDeleteRow] = useState(false);
  const [deletingRowError, setDeletingRowError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [messageSnackbar, setMessageSnackbar] = useState<string | null>(null);
  const [isExistRegulationDomain, setIsExistRegulationDomain] = useState(true);

  const [isRegulationLoading, setIsRegulationLoading] = useState(false);
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isShowUpdateRegulationDialog, setIsShowUpdateRegulationDialog] = useState(false);
  const [selectedRegulation, setSelectedRegulation] = useState<Regulation | { id: string } | null>(
    null
  );
  const [deletingRowType, setDeletingRowType] = useState<
    (typeof REGULATION_ATTRIBUTE_TYPE)[keyof typeof REGULATION_ATTRIBUTE_TYPE] | null
  >(null);
  const [regions, setRegions] = useState<RegulationTableRow[]>([]);

  const apiRef = useGridApiRef();
  const searchRef = useRef("");
  const isExistedRegion = useRef(false);

  const preservedExpandedRows = useRef<PreservedExpandedRow[]>([]);
  const isMounted = useRef(true);
  const [searchParams] = useSearchParams();

  const getTableRows = (): GridRowId[] => {
    return Utils.getAllRowIds(apiRef);
  };

  const fetchRegions = async (isShowLoading = false): Promise<void> => {
    try {
      isShowLoading && setLoading(true);
      setRegions([]);
      const listRegulationRegion = await onGetListRegulationRegion(searchRef.current);

      if (!isExistedRegion.current) {
        isExistedRegion.current = !!listRegulationRegion.length;
      }

      const regionRows: RegulationTableRow[] = listRegulationRegion.map(
        (region: RegulationRegion) => {
          return {
            ...region,
            hierarchy: [region.id],
            type: REGULATION_ATTRIBUTE_TYPE.REGION,
            descendantCount: region.numberOfDomains
          };
        }
      );

      Utils.updateRows(apiRef as any, regionRows);

      // Expand the rows that were previously expanded and are of type REGION
      preservedExpandedRows.current
        .filter(
          (row: PreservedExpandedRow) =>
            row.type === REGULATION_ATTRIBUTE_TYPE.REGION &&
            regionRows.some((r: RegulationTableRow) => r.id === row.id)
        )
        .forEach((row: PreservedExpandedRow) => {
          apiRef.current.setRowChildrenExpansion(row.id, true);
        });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchDomains = async (
    regionId: string,
    node: GridGroupNode,
    row: RegulationTableRow
  ): Promise<void> => {
    const listRegulationDomain = await onGetListRegulationDomain(regionId, searchRef.current);

    setIsExistRegulationDomain(listRegulationDomain.length > 0);

    const domainRows: RegulationTableRow[] = listRegulationDomain.map(
      (domain: RegulationDomain) => {
        return {
          ...domain,
          hierarchy: [...row.hierarchy, domain.id],
          type: REGULATION_ATTRIBUTE_TYPE.DOMAIN,
          descendantCount: domain.numberOfCategories,
          regionRowId: row.id
        };
      }
    );

    Utils.updateRows(apiRef as any, [
      ...domainRows,
      { ...row, childrenFetched: true },
      { id: `placeholder-children-${node.id}`, _action: "delete", hierarchy: [] }
    ]);

    // Expand the rows that were previously expanded and are of type DOMAIN
    const expandedDomains = preservedExpandedRows.current.filter(
      (expandedRow: PreservedExpandedRow) =>
        expandedRow.type === REGULATION_ATTRIBUTE_TYPE.DOMAIN &&
        domainRows.some((r: RegulationTableRow) => r.id === expandedRow.id)
    );

    if (expandedDomains.length) {
      expandedDomains.forEach(async (domainRow: PreservedExpandedRow) => {
        apiRef.current.setRowChildrenExpansion(domainRow.id, true);
      });
    }
  };

  const fetchCategories = async (node: GridGroupNode, row: RegulationTableRow): Promise<void> => {
    const regulationCategories = await onGetListRegulationCategory(row.id, searchRef.current);
    const categoryRows: RegulationTableRow[] = regulationCategories.map(
      (category: RegulationCategory) => ({
        ...category,
        hierarchy: [...row.hierarchy, category.id],
        type: REGULATION_ATTRIBUTE_TYPE.CATEGORY,
        descendantCount: category.numberOfRegulations || 0,
        domainRowId: row.id
      })
    );

    Utils.updateRows(apiRef as any, [
      ...categoryRows,
      { ...row, childrenFetched: true },
      { id: `placeholder-children-${node.id}`, _action: "delete", hierarchy: [] }
    ]);

    // Expand the rows that were previously expanded and are of type CATEGORY
    const expandedCategories = preservedExpandedRows.current.filter(
      (expandedRow: PreservedExpandedRow) =>
        expandedRow.type === REGULATION_ATTRIBUTE_TYPE.CATEGORY &&
        categoryRows.some((r: RegulationTableRow) => r.id === expandedRow.id)
    );

    if (expandedCategories.length) {
      expandedCategories.forEach(async (categoryRow: PreservedExpandedRow) => {
        apiRef.current.setRowChildrenExpansion(categoryRow.id, true);
      });
    }
  };

  const fetchRegulations = async (node: GridGroupNode, row: RegulationTableRow): Promise<void> => {
    const listRegulation = await onGetListRegulationByCategoryId(row.id, searchRef.current);
    if (!listRegulation.length) {
      Utils.updateRows(apiRef, [
        {
          ...row,
          childrenFetched: true,
          descendantCount: 0
        },
        { id: `placeholder-children-${node.id}`, _action: "delete", hierarchy: [] }
      ]);
      return;
    }
    const regulationRows: RegulationTableRow[] = listRegulation.map((regulation: Regulation) => ({
      ...regulation,
      hierarchy: [...row.hierarchy, regulation.id],
      type: REGULATION_ATTRIBUTE_TYPE.REGULATION,
      numberOfRegulationTasks: regulation.regulationTasks.length || 0,
      categoryId: row.id
    }));
    Utils.updateRows(apiRef as any, [
      ...regulationRows,
      { ...row, childrenFetched: true },
      { id: `placeholder-children-${node.id}`, _action: "delete", hierarchy: [] }
    ]);
  };

  useEffect(() => {
    if (isMounted.current) {
      track(ManageRegsEvent.Viewed);
      window.scroll({ top: 0, left: 0, behavior: "smooth" });

      fetchRegions(true);

      isMounted.current = false;
    }

    const unsubscribe = apiRef.current.subscribeEvent(
      "rowExpansionChange",
      handleRowExpansionChange
    );

    return unsubscribe;
  }, [apiRef]);

  const handleRowExpansionChange = async (node: GridGroupNode): Promise<void> => {
    const row = apiRef.current.getRow(node.id) as RegulationTableRow | null;

    if (!node.childrenExpanded || !row || row.childrenFetched) {
      return;
    }

    switch (row.type) {
      case REGULATION_ATTRIBUTE_TYPE.DOMAIN:
        await fetchCategories(node, row);
        break;
      case REGULATION_ATTRIBUTE_TYPE.CATEGORY:
        await fetchRegulations(node, row);
        break;

      default:
        await fetchDomains(row.id, node, row);
        break;
    }

    if (node.childrenExpanded) {
      const existedRow = preservedExpandedRows.current.find(
        (r: PreservedExpandedRow) => r.id === node.id
      );

      if (!existedRow) {
        preservedExpandedRows.current.push(row as PreservedExpandedRow);
      }
    } else {
      preservedExpandedRows.current = preservedExpandedRows.current.filter(
        (r: PreservedExpandedRow) => r.id !== node.id
      );
    }
  };

  const searchDebounce = useCallback(
    debounce((value) => {
      if (value) track(ManageRegsEvent.Search, { searchtext: value });
      fetchRegions(true);
    }, 500),
    []
  );

  const isNotGeneratedChildrenRow = (arrayChildrenRow: string[]): boolean => {
    return arrayChildrenRow.some((id: string) => id.includes("placeholder-children"));
  };

  const handleCLickTag = (
    event: React.MouseEvent<HTMLDivElement>,
    params: GridRenderCellParams
  ): void => {
    event.stopPropagation();
    if ((params.row as RegulationTableRow).numberOfRegulationTasks === 0) {
      return;
    }

    onOpenUpdateRegulationDialog(params.row as RegulationTableRow, true);
  };

  const initialColumns = (columns: TableColumnDef[]): TableColumnDef[] => {
    const initialColumnList: TableColumnDef[] = [];
    if (columns && columns.length > 0) {
      columns.forEach((column: TableColumnDef) => {
        if (column.type === "name") {
          initialColumnList.push({
            ...column,
            filterable: false,
            disableReorder: true,
            sortable: false,
            align: "left",
            valueGetter: (_value, row) => row.name,
            cellClassName: "Klayo_associated-attribute-cell",
            renderCell: (params) => {
              const name = params?.row?.name;
              const isExpanded = (params.rowNode as TreeNodeWithChildren).childrenExpanded;
              const paddingLeft = `${params.rowNode.depth * 24}px`;

              const isLeaf = params.rowNode.type === "leaf";

              return (
                <div
                  style={{
                    fontWeight: isExpanded ? "bold" : "normal",
                    paddingLeft: paddingLeft
                  }}
                  className='truncate'
                >
                  <Tooltip title={name as string}>
                    <span>
                      {isLeaf ? highlightKeyword((name as string) ?? "", search) : (name as string)}
                    </span>
                  </Tooltip>
                </div>
              );
            },
            flex: 1
          });
        } else if (column.type === "actions") {
          const nameColumn: TableColumnDef = {
            ...column,
            filterable: false,
            disableReorder: true,
            align: "right",
            getActions: (params) =>
              (params.row as RegulationTableRow).deleted
                ? []
                : [
                    <GridActionsCellItem
                      icon={<MoreVertIcon />}
                      label='Actions'
                      onClick={(event: React.MouseEvent<HTMLElement>) => {
                        event.stopPropagation();
                        setSelectedRow(params.row as RegulationTableRow);
                        setSelectedRowElement(event.currentTarget);
                      }}
                      key={Math.random().toString(36).substring(7)}
                    />
                  ]
          };
          initialColumnList.push(nameColumn);
        } else if (column.type === "task") {
          initialColumnList.push({
            ...column,
            filterable: false,
            disableReorder: true,
            sortable: false,
            align: "right",
            valueGetter: (_value: unknown, row: unknown) =>
              (row as RegulationTableRow).numberOfRegulationTasks,
            cellClassName: "Klayo_associated-attribute-cell",
            renderCell: (params: GridRenderCellParams) => (
              <KlayoTag
                isTag={
                  (params.row as RegulationTableRow).numberOfRegulationTasks !== 0 &&
                  (params.row as RegulationTableRow).type === REGULATION_ATTRIBUTE_TYPE.REGULATION
                }
                onClick={(event: React.MouseEvent<HTMLDivElement>) => handleCLickTag(event, params)}
              >
                {(params.row as RegulationTableRow).numberOfRegulationTasks}
              </KlayoTag>
            )
          });
        }
      });
    }
    return initialColumnList;
  };

  const renderRowActionMenu = () => {
    if (!selectedRow) return null;

    if (selectedRow.type === REGULATION_ATTRIBUTE_TYPE.REGION) {
      return (
        <MenuItem
          onClick={() => {
            track(ManageRegsEvent.MenuOption, { option: "add_domain" });
            handleOnRowClick(selectedRow);
            setSelectedRowElement(null);
          }}
        >
          <ListItemText>{translate("settingsViews.regulations.addDomain")}</ListItemText>
        </MenuItem>
      );
    }

    return (
      <div>
        <MenuItem
          onClick={() => {
            track(ManageRegsEvent.MenuOption, { option: "edit" });
            handleOnRowClick(selectedRow);
            setSelectedRowElement(null);
          }}
        >
          <ListItemText>{translate("common.edit")}</ListItemText>
        </MenuItem>

        {selectedRow.type === REGULATION_ATTRIBUTE_TYPE.DOMAIN && (
          <MenuItem
            onClick={() => {
              track(ManageRegsEvent.MenuOption, { option: "add_category" });
              onOpenCreateCategoryDialogFromRelatedDomain(selectedRow);
              setSelectedRowElement(null);
            }}
          >
            <ListItemText>{translate("settingsViews.regulations.addCategory")}</ListItemText>
          </MenuItem>
        )}

        {selectedRow.type === REGULATION_ATTRIBUTE_TYPE.CATEGORY && (
          <MenuItem
            onClick={() => {
              track(ManageRegsEvent.MenuOption, { option: "add_regulation" });
              onOpenCreateRegulationFromRelatedCategory(selectedRow);
              setSelectedRowElement(null);
            }}
          >
            <ListItemText>{translate("settingsViews.regulations.addRegulation")}</ListItemText>
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            track(ManageRegsEvent.MenuOption, { option: "delete" });
            onDeleteRow();
          }}
        >
          <ListItemText>{translate("common.delete")}</ListItemText>
        </MenuItem>
      </div>
    );
  };

  const onDeleteRow = () => {
    setSelectedRowElement(null);
    setDeletingRowError(null);
    setDeletingRowType(selectedRow?.type ?? null);
    setIsDeleteRow(true);
  };

  const resetAllMessageError = () => {
    setRegulationDomainError(null);
    setRegulationCategoryError(null);
    setDeletingRowError(null);
  };

  const handleOnRowClick = (dataRow: RegulationTableRow): void => {
    switch (dataRow.type) {
      case REGULATION_ATTRIBUTE_TYPE.DOMAIN:
        onOpenUpdateDomainDialog(dataRow);
        break;

      case REGULATION_ATTRIBUTE_TYPE.CATEGORY:
        onOpenUpdateCategoryDialog(dataRow);
        break;

      case REGULATION_ATTRIBUTE_TYPE.REGULATION:
        onOpenUpdateRegulationDialog(dataRow);
        break;

      default:
        onOpenCreateDomainDialog(dataRow);
        break;
    }
  };

  const onOpenCreateRegulationFromRelatedCategory = (category: RegulationTableRow): void => {
    setIsShowUpdateRegulationDialog(true);
    setSelectedCategory(category);
  };

  const renderDomainAndCategoryActionMenu = () => {
    return (
      <div>
        <MenuItem onClick={() => onOpenCreateDomainDialog()}>
          <ListItemText>{translate("settingsViews.regulations.newDomain")}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => onOpenCreateCategoryDialog()}>
          <ListItemText>{translate("settingsViews.regulations.newCategory")}</ListItemText>
        </MenuItem>
      </div>
    );
  };

  const onOpenCreateDomainDialog = (row?: RegulationTableRow): void => {
    track(ManageRegsEvent.NewDomain);
    setIsShowRegulationDomainDialog(true);
    setCreateRegulationDomainMode(CREATE_TASK_MODE.CREATE);
    setDomainAndCategoryActionMenuElement(null);
    resetAllMessageError();
    row && setSelectedRegion(row);
  };

  const onOpenUpdateDomainDialog = (domain: RegulationTableRow): void => {
    setIsShowRegulationDomainDialog(true);
    setCreateRegulationDomainMode(CREATE_TASK_MODE.EDIT);
    setSelectedDomain(domain);
    setDomainAndCategoryActionMenuElement(null);
    resetAllMessageError();
  };

  const onOpenCreateCategoryDialogFromRelatedDomain = (domain: RegulationTableRow): void => {
    track(ManageRegsEvent.NewCategory);
    setIsShowRegulationCategoryDialog(true);
    setCreateRegulationCategoryMode(CREATE_TASK_MODE.CREATE);
    setSelectedDomain(domain);
    setDomainAndCategoryActionMenuElement(null);
    resetAllMessageError();
  };

  const onOpenCreateCategoryDialog = (): void => {
    track(ManageRegsEvent.NewCategory);
    setIsShowRegulationCategoryDialog(true);
    setCreateRegulationCategoryMode(CREATE_TASK_MODE.CREATE);
    setDomainAndCategoryActionMenuElement(null);
    resetAllMessageError();
  };

  const onOpenUpdateCategoryDialog = (category: RegulationTableRow): void => {
    setIsShowRegulationCategoryDialog(true);
    setCreateRegulationCategoryMode(CREATE_TASK_MODE.EDIT);
    setSelectedCategory(category);
    setDomainAndCategoryActionMenuElement(null);
    resetAllMessageError();
  };

  const onCloseCreateDomainDialog = () => {
    setIsShowRegulationDomainDialog(false);
    setSelectedDomain(null);
    setSelectedRegion(null);
  };

  const onCloseCreateCategoryDialog = () => {
    setIsShowRegulationCategoryDialog(false);
    setSelectedDomain(null);
    setSelectedCategory(null);
    setSelectedDomain(null);
  };

  const onSaveRegulationDomain = async ({
    domain,
    regionId,
    domainId
  }: DomainSaveData): Promise<void> => {
    const { onCreateRegulationDomain, onUpdateRegulationDomain } = props;

    let regulationDomain: RegulationDomain | null = null;

    try {
      setIsRegulationLoading(true);
      if (createRegulationDomainMode === CREATE_TASK_MODE.CREATE && onCreateRegulationDomain) {
        const res = await onCreateRegulationDomain(domain, regionId);
        setIsExistRegulationDomain(true);

        regulationDomain = res.regulationDomain;
      } else if (onUpdateRegulationDomain && domainId) {
        const res = await onUpdateRegulationDomain(domain, domainId, regionId);

        regulationDomain = res.regulationDomain;
      }

      if (regulationDomain) {
        const row: RegulationTableRow = {
          ...regulationDomain,
          hierarchy: [regulationDomain.regulationRegionId, regulationDomain.id],
          type: REGULATION_ATTRIBUTE_TYPE.DOMAIN,
          descendantCount: regulationDomain.numberOfCategories,
          regionRowId: regulationDomain.regulationRegionId
        };

        apiRef.current.updateRows([row]);
      }

      onCloseCreateDomainDialog();
      setMessageSnackbar(translate("settingsViews.regulations.domainSaved"));
    } catch (error) {
      setRegulationDomainError(ErrorModel.parseServerError(error));
    } finally {
      setIsRegulationLoading(false);
    }
  };

  const onSaveRegulationCategory = async ({ domain, category }: Record<string, string>) => {
    const { onCreateRegulationCategory, onUpdateRegulationCategory } = props;

    let regulationCategory = null;

    try {
      setIsCategoryLoading(true);
      if (createRegulationCategoryMode === CREATE_TASK_MODE.CREATE && onCreateRegulationCategory) {
        const res = await onCreateRegulationCategory(domain, category);

        regulationCategory = res.regulationCategory;
      } else if (onUpdateRegulationCategory) {
        const res = await onUpdateRegulationCategory(category, selectedCategory?.id ?? "", domain);

        regulationCategory = res.regulationCategory;
      }

      // Update the parent row's descendant count
      const parentRow = apiRef.current.getRow(domain) as RegulationTableRow | null;
      if (parentRow && createRegulationCategoryMode === CREATE_TASK_MODE.CREATE) {
        apiRef.current.updateRows([
          {
            ...parentRow,
            descendantCount: (parentRow.descendantCount ?? 0) + 1
          }
        ]);
      }

      if (regulationCategory) {
        const row = {
          ...regulationCategory,
          hierarchy: [
            regulationCategory.regulationRegionId,
            regulationCategory.regulationDomainId,
            regulationCategory.id
          ],
          type: REGULATION_ATTRIBUTE_TYPE.CATEGORY,
          descendantCount: regulationCategory.numberOfRegulations,
          domainRowId: regulationCategory.regulationDomainId
        };

        apiRef.current.updateRows([row]);
      }

      onCloseCreateCategoryDialog();
      setMessageSnackbar(translate("settingsViews.regulations.categorySaved"));
    } catch (error) {
      setRegulationCategoryError(ErrorModel.parseServerError(error));
    } finally {
      setIsCategoryLoading(false);
    }
  };

  const onCancelDeleteRegulation = () => {
    setIsDeleteRow(false);
    setDeletingRowType(null);
  };

  const onDeleteRegulationConfirmed = async () => {
    const { onDeleteDomain, onDeleteCategory, onDeleteRegulation } = props;

    if (!selectedRow) return;

    Utils.deleteRow(apiRef as any, selectedRow.id, selectedRow.type);
    if (Utils.getAllRowIds(apiRef as any).length === 0) {
      setIsExistRegulationDomain(false);
    }

    try {
      setIsDeleteLoading(true);
      let successMessage = "";
      if (selectedRow.type === REGULATION_ATTRIBUTE_TYPE.DOMAIN) {
        await onDeleteDomain?.(selectedRow.id);
        successMessage = translate("settingsViews.regulations.domainDeleted");
      } else if (selectedRow.type === REGULATION_ATTRIBUTE_TYPE.CATEGORY) {
        await onDeleteCategory?.(selectedRow.id);
        successMessage = translate("settingsViews.regulations.categoryDeleted");
      } else {
        await onDeleteRegulation?.(selectedRow.id);
        successMessage = translate("settingsViews.regulations.regulationDeleted");
      }
      setIsDeleteRow(false);
      setDeletingRowError(null);
      setDeletingRowType(null);
      setSelectedRowElement(null);
      setMessageSnackbar(successMessage);
    } catch (e) {
      setDeletingRowError(ErrorModel.parseServerError(e));
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const onSearchValueChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ): void => {
    event.preventDefault();
    setSearch(event.target.value);
    searchRef.current = event.target.value;
    searchDebounce(event.target.value);
  };

  const handleOnRowItemClick = (params: GridRowParams, event: SyntheticEvent): void => {
    event.stopPropagation();
    handleOnRowClick(params.row as RegulationTableRow);
    setSelectedRowElement(null);
  };

  const handleDeleteRowText = (selectedRow: RegulationTableRow): string => {
    const row = apiRef.current.getRow(selectedRow.id) as RegulationTableRow | null;

    if (deletingRowType === REGULATION_ATTRIBUTE_TYPE.DOMAIN) {
      if (row && row.descendantCount && row.descendantCount > 0) {
        return translate("settingsViews.regulations.deleteCategoriesFirst");
      } else {
        return translate("settingsViews.regulations.deleteDomainConfirm");
      }
    } else if (deletingRowType === REGULATION_ATTRIBUTE_TYPE.CATEGORY) {
      return translate("settingsViews.regulations.deleteCategoryConfirm");
    } else {
      return translate("settingsViews.regulations.deleteRegulationConfirm");
    }
  };

  const onOpenUpdateRegulationDialog = (
    regulation: RegulationTableRow,
    isTagClick?: boolean
  ): void => {
    setDomainAndCategoryActionMenuElement(null);
    resetAllMessageError();

    if (isTagClick) {
      history.push(`/settings/regulations?regulationId=${regulation.id}&navigateStep=2`);
      return;
    }
    history.push(`/settings/regulations?regulationId=${regulation.id}`);
  };

  const onCloseUpdateRegulationDialog = () => {
    setIsShowUpdateRegulationDialog(false);
    setSelectedRegulation(null);
    setSelectedCategory(null);
    history.push(`/settings/regulations`);
  };

  const onSaveRegulationSuccess = (regulation: Regulation): void => {
    const updatedRow: RegulationTableRow = {
      ...regulation,
      hierarchy: [
        regulation.regulationRegionId,
        regulation.regulationDomainId,
        regulation.regulationCategoryId,
        regulation.id
      ],
      type: REGULATION_ATTRIBUTE_TYPE.REGULATION,
      numberOfRegulationTasks: regulation.regulationTasks.length,
      categoryRowId: regulation.regulationCategoryId
    };
    apiRef.current.updateRows([updatedRow]);

    onCloseUpdateRegulationDialog();
  };

  useEffect(() => {
    const regulationId = searchParams.get("regulationId");

    if (regulationId) {
      setSelectedRegulation({
        id: regulationId
      });

      setIsShowUpdateRegulationDialog(true);
    }
  }, [searchParams]);

  return (
    <div>
      <Paper
        theme={theme}
        padding={{ xs: "46px 24px", md: "60px!important" }}
        borderFromBreakpoint='md'
      >
        <h1>{translate("settingsViews.regulations.title")}</h1>
        <div>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            spacing={2}
            sx={{ borderTop: "1px solid rgba(0, 0, 0, 0.12)", padding: "30px 0" }}
          >
            <TextField
              value={search}
              dense={true}
              placeholder={translate("common.search")}
              disabled={user === null}
              fullWidth={true}
              autoComplete={false}
              leadingIcon={<SearchIcon />}
              onChange={onSearchValueChange}
              sx={{ maxWidth: { md: "300px" } }}
            />

            <div
              className={`klaro_regulations-button-container ${!isExistedRegion.current ? "disabled-container" : ""}`}
            >
              <Button
                size='md'
                theme={theme}
                variant='filled'
                showLabelFromBreakpoint='md'
                startIcon={<AddIcon />}
                label={translate("settingsViews.regulations.newRegulation")}
                disabled={!isExistedRegion.current}
                className='klaro_regulations-button-add-regulation'
                onClick={() => {
                  track(ManageRegsEvent.NewRegulation);
                  setIsShowUpdateRegulationDialog(true);
                }}
              />
              <div className='klaro_regulations-vertical-line'></div>
              <div className='klaro_regulations-domain-category'>
                <ChevronDownWhiteIcon
                  onClick={(event: React.MouseEvent<SVGSVGElement>) => {
                    if (isExistedRegion.current) {
                      setDomainAndCategoryActionMenuElement(
                        event.currentTarget as unknown as HTMLElement
                      );
                    }
                  }}
                />
              </div>
            </div>
          </Stack>

          {/* No row component */}

          {loading ? (
            <SectionLoader />
          ) : !isExistedRegion.current ? (
            <TablePlaceholder
              text={translate("settingsViews.regulations.noRegulationsPlaceholder")}
              icon={<AttributesIcon />}
            />
          ) : !getTableRows()?.length && searchRef.current ? (
            <TablePlaceholder text={translate("common.noRows")} icon={<AttributesIcon />} />
          ) : null}

          {/* Table */}
          <RegulationsTable
            {...{
              ref: apiRef,
              initialRows: regions,
              initialColumns,
              theme,
              handleOnRowItemClick,
              showHeader: false,
              hideFirstLastBorder: true,
              tableStyle:
                loading || (!getTableRows()?.length && searchRef.current)
                  ? "klaro_regulations-display-none"
                  : "",
              keyword: search
            }}
          />
        </div>

        {isShowRegulationDomainDialog && (
          <CreateRegulationDomainDialog
            {...({
              user,
              employee: user,
              theme,
              onClose: onCloseCreateDomainDialog,
              onSave: onSaveRegulationDomain,
              error: regulationDomainError ?? undefined,
              mode: createRegulationDomainMode,
              selectedDomain,
              selectedRegion,
              loading: isRegulationLoading,
              onGetListRegulationRegion
            } as any)}
          />
        )}

        {isShowRegulationCategoryDialog && (
          <CreateRegulationCategoryDialog
            {...({
              user,
              employee: user,
              theme,
              onClose: onCloseCreateCategoryDialog,
              onSave: onSaveRegulationCategory,
              error: regulationCategoryError ?? undefined,
              mode: createRegulationCategoryMode,
              listRegulationDomain: listAllRegulationDomain,
              selectedCategory,
              selectedDomain,
              loading: isCategoryLoading,
              onGetListRegulationRegion
            } as any)}
          />
        )}
        {isShowUpdateRegulationDialog && (
          <UpdateRegulationDialog
            {...({
              theme,
              onClose: onCloseUpdateRegulationDialog,
              onSaveRegulation,
              listRegulationDomain: listAllRegulationDomain,
              selectedRegulation,
              onGetListRegulationRegion,
              onGetListRegulationDomain,
              onGetListRegulationCategory,
              onUpdateRegulation,
              onSaveRegulationSuccess,
              onBlockNavigation,
              onAllowNavigation,
              onNavigation,
              selectedCategory
            } as any)}
          />
        )}

        {isDeleteRow && selectedRow && (
          <ConfirmationDialog
            theme={theme}
            title={`${translate("common.delete")} ${
              selectedRow.type === REGULATION_ATTRIBUTE_TYPE.DOMAIN
                ? translate("settingsViews.regulations.domain")
                : selectedRow.type === REGULATION_ATTRIBUTE_TYPE.CATEGORY
                  ? translate("settingsViews.regulations.category")
                  : translate("settingsViews.regulations.regulation")
            }`}
            question={<div>{handleDeleteRowText(selectedRow)}</div>}
            acceptButton={translate("common.delete")}
            acceptDanger={true}
            error={deletingRowError}
            onCancel={onCancelDeleteRegulation}
            onAccept={onDeleteRegulationConfirmed}
            isNotShowAcceptButton={
              selectedRow.type === REGULATION_ATTRIBUTE_TYPE.DOMAIN &&
              (selectedRow.descendantCount ?? 0) > 0
            }
            loading={isDeleteLoading}
          />
        )}

        <StyledMenu
          open={domainAndCategoryActionMenuElement !== null}
          anchorEl={domainAndCategoryActionMenuElement}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
          onClose={() => {
            setDomainAndCategoryActionMenuElement(null);
          }}
        >
          {renderDomainAndCategoryActionMenu()}
        </StyledMenu>

        <StyledMenu
          open={selectedRowElement !== null}
          anchorEl={selectedRowElement}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
          onClose={() => setSelectedRowElement(null)}
        >
          {renderRowActionMenu()}
        </StyledMenu>

        {messageSnackbar && (
          <Snackbar
            open={true}
            duration={5000}
            onClose={() => setMessageSnackbar(null)}
            type='success'
            message={messageSnackbar}
          />
        )}
      </Paper>
    </div>
  );
}

const StyledMenu = styled(Menu)`
  .MuiMenu-paper {
    margin-top: 10px !important;
  }
`;

const KlayoTag = styled.div<KlayoTagProps>`
  padding: 2px 12px;
  background-color: ${(props) =>
    props.isTag ? KLAYO_COLORS.NeonBlueNB050 : KLAYO_COLORS.transparent};
  border-radius: 100px;

  color: ${(props) => (props.isTag ? KLAYO_COLORS.NeonBlueNB500 : KLAYO_COLORS.GreyGR900)};
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;

  :hover {
    filter: ${(props) => (props.isTag ? "brightness(95%)" : "")};
    cursor: ${(props) => (props.isTag ? "pointer" : "")};
  }

  :focus {
    background-color: ${(props) => (props.isTag ? "#c6c7fc" : "")};
  }
`;
